import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Truck, MapPin, User, ReceiptPoundSterling, BadgeCheck, ShieldCheck, CheckCircle2, FileDown, Clock } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import OrderStageTimeline from '@/components/OrderStageTimeline';
import OrderStageActionModal from '@/components/OrderStageActionModal';
import OrderDeleteButton from '@/components/OrderDeleteButton';
import { formatApiError } from '@/utils/apiError';
import { InvoiceExpiredNotice } from '@/components/orders/InvoiceExpiredNotice';
import { ResendOrderEmailCard } from '@/components/orders/ResendOrderEmailCard';

const PRODECOR_PRIMARY = '#D5AB09';
const PRODECOR_SECONDARY = '#2B2101';

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const CATEGORY_LABEL = {
  primer: 'Primers', finish: 'Finishes', pigment: 'Pigments', wax: 'Waxes', sealer: 'Sealers',
};

export default function ProDecorOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageAction, setStageAction] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [expectedEta, setExpectedEta] = useState('');
  const [submittingTransition, setSubmittingTransition] = useState(false);
  // Customer-quote modal state
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    customer_name: '',
    customer_address: '',
    customer_phone: '',
    project_title: '',
    markup_pct: 0,
    quote_valid_days: 30,
    notes: '',
    show_vat: false,
  });

  const load = useCallback(() => {
    setLoading(true);
    return fetch(`${API_URL}/api/prodecor-orders/${orderId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(setOrder)
      .finally(() => setLoading(false));
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading order…</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center text-gray-500">Order not found.</div>;

  const status = order.status || 'submitted';
  const closeStageModal = () => {
    if (submittingTransition) return;
    setStageAction(null); setInvoiceNumber(''); setPaymentMethod(''); setExpectedEta('');
  };

  const submitTransition = async (targetStatus, extras = {}) => {
    setSubmittingTransition(true);
    try {
      const res = await fetch(`${API_URL}/api/prodecor-orders/${orderId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: targetStatus, ...extras }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Update failed');
      }
      toast.success(`Order marked as ${targetStatus.replace('_', ' ')}`);
      closeStageModal();
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingTransition(false);
    }
  };

  const downloadCustomerQuote = async () => {
    if (!quoteForm.customer_name.trim()) {
      toast.error('Enter a customer name to generate the quote.');
      return;
    }
    setQuoteBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/prodecor-orders/${orderId}/quote-pdf`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...quoteForm,
          markup_pct: Number(quoteForm.markup_pct) || 0,
          quote_valid_days: Number(quoteForm.quote_valid_days) || 30,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'PDF generation failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${order.reference}-quote.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Customer quote PDF downloaded');
      setQuoteOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setQuoteBusy(false);
    }
  };

  const stageActionButtons = (() => {
    const a = {};
    if (isStaff && status === 'submitted') a.raise_invoice = (
      <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-800 whitespace-nowrap" data-testid="prodecor-raise-invoice-btn" onClick={() => navigate(`/staff/invoices?from=prodecor&order=${order.id}`)}>
        <FileText className="w-3.5 h-3.5 mr-1" /> Raise invoice in Xero
      </Button>
    );
    if (isStaff && status === 'submitted') a.invoiced = (
      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap" data-testid="prodecor-mark-invoiced-btn" onClick={() => setStageAction('invoiced')}>
        <ReceiptPoundSterling className="w-3.5 h-3.5 mr-1" /> Mark as invoiced
      </Button>
    );
    if (!isStaff && status === 'invoiced') a.paid = (
      <Button size="sm" className={order.confirmed_total != null ? "bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap" : "bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 whitespace-nowrap"} data-testid="prodecor-mark-paid-btn" onClick={() => setStageAction('paid')}>
          {order.confirmed_total != null ? <><BadgeCheck className="w-3.5 h-3.5 mr-1" /> I've paid — mark as paid</> : <><Clock className="w-3.5 h-3.5 mr-1" /> Awaiting confirmed amount</>}
        </Button>
    );
    if (isStaff && status === 'paid') a.payment_confirmed = (
      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap" data-testid="prodecor-mark-payment-confirmed-btn" onClick={() => setStageAction('payment_confirmed')}>
        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Mark as payment confirmed
      </Button>
    );
    if (isStaff && (status === 'payment_confirmed' || status === 'paid')) a.dispatched = (
      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap" data-testid="prodecor-mark-dispatched-btn" onClick={() => setStageAction('dispatched')}>
        <Truck className="w-3.5 h-3.5 mr-1" /> Mark as dispatched
      </Button>
    );
    if (!isStaff && status === 'dispatched') a.delivered = (
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap" data-testid="prodecor-mark-delivered-btn" onClick={() => setStageAction('delivered')}>
        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark as delivered
      </Button>
    );
    return a;
  })();

  const handleConfirm = (extra = {}) => {
    if (stageAction === 'invoiced') submitTransition('invoiced', { invoice_number: invoiceNumber.trim(), ...extra });
    else if (stageAction === 'payment_confirmed') submitTransition('payment_confirmed', { payment_method: paymentMethod });
    else if (stageAction === 'dispatched') submitTransition('dispatched', expectedEta ? { expected_delivery_date: expectedEta } : {});
    else submitTransition(stageAction, extra);
  };

  // Group items by category
  const grouped = {};
  (order.items || []).forEach((it) => {
    grouped[it.category] = grouped[it.category] || [];
    grouped[it.category].push(it);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: `linear-gradient(135deg, ${PRODECOR_SECONDARY}, #4a3a0d)` }}>
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center gap-4">
          <button onClick={() => navigate('/prodecor/orders')} className="p-2 hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold">{order.reference}</h1>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20">
                {order.status}
              </span>
            </div>
            <p className="text-white/70 text-sm">Order details</p>
          </div>
          <div className="ml-auto text-right flex items-center gap-3">
            <OrderDeleteButton order={order} product="prodecor" backTo="/prodecor/orders" />
            <Button
              type="button"
              onClick={() => {
                // Prefill customer name from the order's customer_reference (installers
                // usually type a name in that field when building the basket).
                setQuoteForm(prev => ({
                  ...prev,
                  customer_name: prev.customer_name || order.customer_reference || '',
                }));
                setQuoteOpen(true);
              }}
              size="sm"
              data-testid="prodecor-generate-customer-quote-btn"
              className="bg-white text-[#2B2101] hover:bg-white/90 font-bold"
            >
              <FileDown className="w-4 h-4 mr-1.5" /> Customer Quote PDF
            </Button>
            <div>
              <div className="text-xs text-white/60 uppercase tracking-wider">{order.confirmed_total != null ? 'Confirmed by Ox Resin' : 'Estimate · to be confirmed'}</div>
              <div className="text-2xl font-bold" style={{ color: PRODECOR_PRIMARY }} data-testid="prodecor-detail-total">{fmtGBP(order.confirmed_total ?? order.total)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <InvoiceExpiredNotice order={order} product="prodecor" isStaff={isStaff} onReactivated={load} />
        <Card className="p-4" data-testid="prodecor-stage-timeline-card">
          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Order Stages</h2>
          <OrderStageTimeline order={order} actions={stageActionButtons} />
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
              <User className="w-3 h-3" /> Installer
            </div>
            <div className="font-semibold text-gray-900">{order.installer_name}</div>
            <div className="text-xs text-gray-600">{order.installer_email}</div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
              <MapPin className="w-3 h-3" /> Delivery
            </div>
            <div className="text-sm text-gray-900">{order.company_name}</div>
            <div className="text-xs text-gray-600 whitespace-pre-line">{order.delivery_address || '—'}</div>
            {order.delivery_phone && <div className="text-xs text-gray-600">{order.delivery_phone}</div>}
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 mb-2">
              <Truck className="w-3 h-3" /> Notes
            </div>
            <div className="text-sm"><strong>Ref:</strong> {order.customer_reference || '—'}</div>
            <div className="text-xs text-gray-600 mt-1">{order.delivery_notes || 'No delivery notes'}</div>
          </Card>
        </div>

        {Object.entries(CATEGORY_LABEL).map(([cat, label]) => {
          const items = grouped[cat];
          if (!items || items.length === 0) return null;
          return (
            <Card key={cat} className="p-5">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#D5AB09]" /> {label}
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
                    <th className="py-2 pr-2">Qty</th>
                    <th className="py-2 pr-2">Product</th>
                    <th className="py-2 pr-2 text-right">Unit</th>
                    <th className="py-2 pr-2 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={`${cat}-${it.product_id || it.product_name}-${idx}`} className="border-b last:border-0">
                      <td className="py-2 pr-2 font-semibold">{it.qty}×</td>
                      <td className="py-2 pr-2">
                        <div>{it.product_name}</div>
                        <div className="text-xs text-gray-500">{it.pack_label}</div>
                      </td>
                      <td className="py-2 pr-2 text-right text-gray-600">{fmtGBP(it.unit_price)}</td>
                      <td className="py-2 pr-2 text-right font-semibold">{fmtGBP(it.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          );
        })}

        {isStaff && <ResendOrderEmailCard product="prodecor" order={order} accent={PRODECOR_SECONDARY} testIdPrefix="pd-resend" onSent={(d) => setOrder((o) => ({ ...o, email_resent_at: d.email_resent_at }))} />}

        <Card className="p-5" style={{ backgroundColor: PRODECOR_SECONDARY }}>
          <div className="flex items-center justify-between text-white">
            <div className="text-xs uppercase tracking-wider opacity-70">{order.confirmed_total != null ? 'Confirmed total (inc. VAT)' : 'Estimate (excl. VAT)'}</div>
            <div className="text-2xl font-bold" style={{ color: PRODECOR_PRIMARY }}>{fmtGBP(order.confirmed_total ?? order.total)}</div>
          </div>
          {order.confirmed_total == null && !isStaff && ['submitted', 'invoiced'].includes(order.status) && (
            <p className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="prodecor-dont-pay-notice"><strong>Please don't pay yet.</strong> This is the app's estimate — the Ox Resin office will confirm the exact amount to pay and you'll see it here as <em>Confirmed</em>.</p>
          )}
        </Card>
      </div>

      <OrderStageActionModal
        action={stageAction}
        order={order}
        invoiceNumber={invoiceNumber}  setInvoiceNumber={setInvoiceNumber}
        paymentMethod={paymentMethod}  setPaymentMethod={setPaymentMethod}
        expectedEta={expectedEta}      setExpectedEta={setExpectedEta}
        submitting={submittingTransition}
        isStaff={isStaff}
        onClose={closeStageModal}
        onConfirm={handleConfirm}
        accentClass="bg-[#D5AB09] hover:bg-[#b09008]"
      />

      <Dialog open={quoteOpen} onOpenChange={(o) => { if (!quoteBusy) setQuoteOpen(o); }}>
        <DialogContent data-testid="prodecor-customer-quote-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Customer Quote PDF</DialogTitle>
            <DialogDescription>
              Turn this ProDecor order into a branded quote you can send to the customer.
              Optionally add a markup to bake in your margin before they see the price.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="quote-customer-name">Customer name *</Label>
              <Input
                id="quote-customer-name"
                data-testid="quote-customer-name"
                value={quoteForm.customer_name}
                onChange={(e) => setQuoteForm(f => ({ ...f, customer_name: e.target.value }))}
                placeholder="e.g. Jane Smith"
              />
            </div>
            <div>
              <Label htmlFor="quote-customer-address">Customer address</Label>
              <Textarea
                id="quote-customer-address"
                data-testid="quote-customer-address"
                value={quoteForm.customer_address}
                onChange={(e) => setQuoteForm(f => ({ ...f, customer_address: e.target.value }))}
                placeholder="Street, city, postcode"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="quote-customer-phone">Customer phone</Label>
                <Input
                  id="quote-customer-phone"
                  data-testid="quote-customer-phone"
                  value={quoteForm.customer_phone}
                  onChange={(e) => setQuoteForm(f => ({ ...f, customer_phone: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label htmlFor="quote-project-title">Project title</Label>
                <Input
                  id="quote-project-title"
                  data-testid="quote-project-title"
                  value={quoteForm.project_title}
                  onChange={(e) => setQuoteForm(f => ({ ...f, project_title: e.target.value }))}
                  placeholder="e.g. Kitchen floor"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="quote-markup">Markup %</Label>
                <Input
                  id="quote-markup"
                  data-testid="quote-markup-pct"
                  type="number"
                  min={0}
                  step={0.5}
                  value={quoteForm.markup_pct}
                  onChange={(e) => setQuoteForm(f => ({ ...f, markup_pct: e.target.value }))}
                />
                <p className="text-[11px] text-gray-500 mt-1">Applied to every line total before the customer sees it.</p>
              </div>
              <div>
                <Label htmlFor="quote-valid-days">Valid for (days)</Label>
                <Input
                  id="quote-valid-days"
                  data-testid="quote-valid-days"
                  type="number"
                  min={1}
                  value={quoteForm.quote_valid_days}
                  onChange={(e) => setQuoteForm(f => ({ ...f, quote_valid_days: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="quote-notes">Notes to customer</Label>
              <Textarea
                id="quote-notes"
                data-testid="quote-notes"
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional — e.g. lead times, access requirements"
                rows={2}
              />
            </div>
            {/* VAT display toggle — default OFF (retail-friendly single figure) */}
            <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="prodecor-vat-toggle-row">
              <div className="flex-1 min-w-0 pr-3">
                <p className="text-sm font-semibold text-[#27292E]">Show VAT breakdown</p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {quoteForm.show_vat
                    ? 'Displays Subtotal, VAT @ 20% and Total inc. VAT — for trade / VAT-registered customers.'
                    : 'Displays a single VAT-inclusive figure with no mention of VAT — recommended for retail customers.'}
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!!quoteForm.show_vat}
                  onChange={(e) => setQuoteForm(f => ({ ...f, show_vat: e.target.checked }))}
                  data-testid="prodecor-show-vat-toggle"
                />
                <div className="relative w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-[#D5AB09] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteOpen(false)} disabled={quoteBusy}>
              Cancel
            </Button>
            <Button
              onClick={downloadCustomerQuote}
              disabled={quoteBusy || !quoteForm.customer_name.trim()}
              data-testid="quote-download-btn"
              className="bg-[#2B2101] hover:bg-[#3a2c02] text-white"
            >
              <FileDown className="w-4 h-4 mr-1.5" />
              {quoteBusy ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
